import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import PageBlock from 'app/shared/componentsRestyled/common/PageBlock';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  getApplicationBorrowersAction,
  getLoanDenialReasonsByLoanIdAction,
  updateLoanDenialReasonsAction,
} from 'store/loanApplications/actions';
import {
  selectFlatLoanDenialReasons,
  selectLoanApplicationBorrowerOptions,
} from 'store/loanApplications/selectors';
import { getDenialReasonsAction, getHMDACodesAction } from 'store/system/actions';
import { selectHMDACodeMap } from 'store/system/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import useLoanDenialForm, { getBorrowerFieldName } from './hooks/useLoanDenialForm';
import { getNotFoundTableData, getTableConfig } from './LoanDenial.config';
import { tableWrapperStyles } from './LoanDenial.styles';
import {
  addFormToTableData,
  createNewTableData,
  normalizeLoanDenialFormData,
} from './LoanDenial.utils';

const LoanDenial = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();
  const { applicationId } = useParams();

  const fetchData = useCallback(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getApplicationBorrowersAction({ applicationId })),
      loanId && dispatch(getLoanDenialReasonsByLoanIdAction({ loanId })),
      dispatch(getDenialReasonsAction()),
      dispatch(getHMDACodesAction()),
    ])
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, applicationId, loanId, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  const { isEditAllowed, isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_DENIAL),
  );

  const loanDenialReasons = useSelector(selectFlatLoanDenialReasons);
  const borrowerOptions = useSelector(selectLoanApplicationBorrowerOptions);
  const HMDACodeMap = useSelector(selectHMDACodeMap);

  const [loanDenialReasonsData, setLoanDenialReasonsData] = useState(loanDenialReasons);
  const [editableLoanDenialData, setEditableLoanDenialData] = useState(null);

  const isEditActive = !!editableLoanDenialData;

  useEffect(() => {
    if (!!loanDenialReasonsData.length || _.isEqual(loanDenialReasons, loanDenialReasonsData)) {
      return;
    }

    setLoanDenialReasonsData(loanDenialReasons);
  }, [loanDenialReasons, loanDenialReasonsData]);

  const addNewLoanDenial = useCallback(() => {
    const entityData = createNewTableData();

    setLoanDenialReasonsData(prevState => [entityData, ...prevState]);

    setEditableLoanDenialData(entityData);
  }, []);

  const updateAction = useCallback(
    body => {
      setIsLoading(true);

      dispatch(updateLoanDenialReasonsAction({ loanId, body }))
        .then(() => {
          dispatch(
            showMessageAction({ message: 'Loan Denial Reasons was updated', variant: 'success' }),
          );

          setEditableLoanDenialData(null);
          setLoanDenialReasonsData([]);
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [loanId, dispatch, setIsLoading],
  );

  const deleteLoanDenial = useCallback(
    ({ id: deletedId }) => {
      const dataToSave = normalizeLoanDenialFormData({
        form: loanDenialReasons.filter(({ id }) => id !== deletedId),
      });

      updateAction(dataToSave);
    },
    [loanDenialReasons, updateAction],
  );

  const cancelForm = useCallback(data => {
    if (data.isNewlyAdded) {
      setLoanDenialReasonsData(prevState => prevState.filter(({ id }) => id !== data.id));
    }

    setEditableLoanDenialData(null);
  }, []);

  const saveForm = useCallback(
    ({ initialId, updatedData: updatedDataForm, isAdded }) => {
      const updatedItem = {
        denialReason: {
          id: updatedDataForm.id ?? initialId,
        },
        selectedBorrowers: borrowerOptions
          .map(({ value }, i) => updatedDataForm[getBorrowerFieldName(i)] && value)
          .filter(Boolean),
      };

      const form = isAdded
        ? [updatedItem, ...loanDenialReasons]
        : loanDenialReasons.map(denial => (denial.id === initialId ? updatedItem : denial));

      const dataToSave = normalizeLoanDenialFormData({ form });

      updateAction(dataToSave);
    },
    [loanDenialReasons, borrowerOptions, updateAction],
  );

  const {
    form,
    handleChange,
    validationErrors,
    isFormChanged,
    onSubmit,
    onCancel,
    onLoanDenialReasonChange,
    filteredDenialReasonOptions,
    isDenialReasonSelected,
  } = useLoanDenialForm({
    editData: editableLoanDenialData,
    borrowerOptions,
    saveForm,
    cancelForm,
  });

  const tableConfig = useMemo(
    () =>
      getTableConfig({
        borrowerOptions,
        HMDACodeMap,
        isActionDisabled: isLoading || !isEditAllowed,
        isEditAllowed,
        editableRowId: editableLoanDenialData?.id,
        setEditableTableData: setEditableLoanDenialData,
        deleteLoanDenial,
      }),
    [
      isLoading,
      isEditAllowed,
      borrowerOptions,
      HMDACodeMap,
      editableLoanDenialData?.id,
      deleteLoanDenial,
    ],
  );

  const correctedTableData = useMemo(() => {
    const loanDenialReasonsWithForm = editableLoanDenialData
      ? addFormToTableData({
          tableData: loanDenialReasonsData,
          editData: editableLoanDenialData,
          form,
          handleChange,
          validationErrors,
          isFormChanged,
          onCancel,
          onLoanDenialReasonChange,
          filteredDenialReasonOptions,
          isDenialReasonSelected,
          borrowerOptions,
          isLoading,
        })
      : loanDenialReasonsData;

    return loanDenialReasonsData.length
      ? loanDenialReasonsWithForm
      : getNotFoundTableData(borrowerOptions.length);
  }, [
    isLoading,
    loanDenialReasonsData,
    editableLoanDenialData,
    borrowerOptions,
    form,
    handleChange,
    validationErrors,
    isFormChanged,
    onCancel,
    onLoanDenialReasonChange,
    filteredDenialReasonOptions,
    isDenialReasonSelected,
  ]);

  return (
    <PageBlock
      withAction
      withDivider
      actionContent={
        <Button
          data-testid={UnderwritingTestIds.LOAN_DENIAL_ADD_BUTTON}
          disabled={!isManageAllowed || isEditActive || isLoading}
          startIcon={<AddBoxIcon />}
          onClick={addNewLoanDenial}
        >
          Add
        </Button>
      }
      title="Denial Reasons"
    >
      <form
        noValidate
        data-ischanged={isFormChanged}
        data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM}
        onSubmit={onSubmit}
      >
        <TableCDS
          config={tableConfig}
          customStyles={tableWrapperStyles}
          data={correctedTableData}
          data-testid={UnderwritingTestIds.LOAN_DENIAL_TABLE}
          isCustomDataDefinition={!loanDenialReasons.length}
          isLoading={isLoading}
          pendingChangesCount={0}
        />
      </form>
    </PageBlock>
  );
};

LoanDenial.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(LoanDenial);
